import addToCart from '../../lib/addToCart';
import renderMinicart from '../../lib/renderMinicart';
import { initSwiper, initSwiperMobile } from '../../components/swiper';
import { CLASSES, SELECTORS } from '../../utils/constants';
import reinitOkendoWidgets from '../../utils/reinitOkendoWidgets';

const initRelatedProducts = ({
  containerSelector,
  contentSelector = null,
  renderSelector = null,
  swiperSelector = '[data-swiper]',
  swiperMobile = false,
  onAddSuccess = null
} = {}) => {
  const containerEl = document.querySelector(containerSelector);
  if (!containerEl) return;

  const productsUrl = containerEl.dataset.url;
  if (!productsUrl) return;

  const contentEl = contentSelector ? containerEl.querySelector(contentSelector) : containerEl;

  const setBtnLoadingState = (button, spinner, loading) => {
    button.classList.toggle(CLASSES.POINTER_EVENTS_NONE, loading);
    button.classList.toggle(CLASSES.OPACITY_50, loading);
    if (spinner) spinner.classList.toggle('hidden', !loading);
  };

  const initSwiperForSlider = () => {
    const sliderEl = containerEl.querySelector(swiperSelector);
    if (!sliderEl) return;

    const slideCount = sliderEl.querySelectorAll('.swiper-slide').length;
    const config = JSON.parse(sliderEl.dataset.swiperConfig || '{}');
    const breakpointViews = Object.values(config.breakpoints || {}).map(bp => bp.slidesPerView || 0);
    const maxSlidesPerView = Math.max(config.slidesPerView || 0, ...breakpointViews);
    const canLoop = slideCount > maxSlidesPerView;

    config.loop = canLoop;
    sliderEl.dataset.swiperConfig = JSON.stringify(config);

    const navContainer = containerEl.querySelector('[data-swiper-nav]');
    if (navContainer) {
      navContainer.classList.toggle('lg:flex', canLoop);
      navContainer.classList.toggle('lg:hidden', !canLoop);
    }

    if (swiperMobile) {
      const swiperInstance = initSwiperMobile(sliderEl);
      window.theme.swiperMobileObjects.push(swiperInstance);
    } else {
      initSwiper(sliderEl);
    }
  };

  const fetchAndRender = async () => {
    containerEl.classList.add('opacity-70', CLASSES.POINTER_EVENTS_NONE);

    try {
      const response = await fetch(productsUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const html = await response.text();

      if (renderSelector) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const renderEl = doc.querySelector(renderSelector);
        const markup = renderEl?.innerHTML?.trim() || '';

        if (!markup) {
          containerEl.classList.add('hidden');
          return;
        }

        contentEl.innerHTML = markup;
        containerEl.classList.remove('hidden');
      } else {
        containerEl.innerHTML = html;
      }

      initSwiperForSlider();
      reinitOkendoWidgets();
    } catch (error) {
      console.error('Failed to fetch related products:', error);
      containerEl.classList.add('hidden');
    } finally {
      containerEl.classList.remove('opacity-70', CLASSES.POINTER_EVENTS_NONE);
    }
  };

  if (onAddSuccess) {
    let isAdding = false;

    containerEl.addEventListener('submit', async (evt) => {
      const form = evt.target.closest('form[data-product-form]');
      if (!form || !containerEl.contains(form)) return;

      evt.preventDefault();
      evt.stopPropagation();

      const submitBtn = evt.submitter || form.querySelector('button[type="submit"]');
      if (!submitBtn || submitBtn.disabled || isAdding) return;
      if (window.theme?.mobileVariantPicker?.openFromForm?.(form)) return;

      const selectedVariantId = +(form.elements?.variant?.value || form.elements?.id?.value || 0);
      if (!selectedVariantId) return;

      isAdding = true;

      const loadingSpinner = submitBtn.querySelector(SELECTORS.LOADING_SPINNER);
      setBtnLoadingState(submitBtn, loadingSpinner, true);

      try {
        const { item, cart } = await addToCart(selectedVariantId, 1);
        await onAddSuccess({ item, cart });
      } catch (error) {
        console.error('Failed to add related product:', error);
      } finally {
        isAdding = false;
        setBtnLoadingState(submitBtn, loadingSpinner, false);
      }
    }, true);
  }

  fetchAndRender();
};

export default initRelatedProducts;
